import React, { useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogContent,
  DialogTitle, Divider, Stack, TextField, Typography
} from '@mui/material';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import { useTranslation } from 'react-i18next';
import CloseButton from './Common/CloseButton';
import { askActivityAssistant, getActionHistoryScope } from '../Utils/actionsApi';
import { ACTION_OUTCOMES, getActionLogOutcome } from '../Utils/actionLogPresentation';
import { normalizeActionLogTimestamp } from '../Utils/timestampUtils';

const formatActivityDate = (value, language, t, log) => {
  const date = new Date(normalizeActionLogTimestamp(value, log));
  return !value || Number.isNaN(date.getTime()) ? t('actionsLogs.activity.dateUnknown', 'Date not recorded')
    : date.toLocaleString(language, { dateStyle: 'medium', timeStyle: 'short' });
};

const Evidence = ({ record, t, language, expanded, onToggle }) => {
  const outcome = getActionLogOutcome(record);
  const detailsRecord = { ...record, timestamp: record.local_timestamp || record.timestamp };
  delete detailsRecord.local_timestamp;
  return <Box sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 2 }}>
    <Typography variant="body2" fontWeight={600}>{record.action_type === 'retry_scheduled'
      ? t('actionsLogs.activity.actions.retry', 'Automatic retry scheduled')
      : record.action_name || t('activityAssistant.activity', 'Recorded activity')}</Typography>
    <Typography variant="caption" color="text.secondary">
      {formatActivityDate(record.local_timestamp || record.timestamp, language, t, record)}
      {' · '}{t(`actionsLogs.activity.outcomes.${outcome}`, ACTION_OUTCOMES[outcome][0])}
    </Typography>
    {record.partial && <Typography variant="caption" display="block">{t('activityAssistant.partial', 'Only part of this activity was recorded or included.')}</Typography>}
    <Box component="details" open={expanded} sx={{ mt: 1, overflowWrap: 'anywhere' }}>
      <Typography component="summary" variant="caption" sx={{ cursor: 'pointer' }} onClick={event => { event.preventDefault(); onToggle(); }}>
        {t('activityAssistant.recordedDetails', 'Recorded details')}
      </Typography>
      <Typography component="pre" variant="caption" sx={{ whiteSpace: 'pre-wrap', maxHeight: 240, overflow: 'auto' }}>
        {JSON.stringify(detailsRecord, null, 2)}
      </Typography>
    </Box>
  </Box>;
};

const Answer = ({ response, t, language, expanded, onToggle }) => {
  const cited = new Set(response.findings.flatMap(finding => finding.evidence_ids));
  const records = response.evidence.filter(record => cited.has(record.id));
  return <Stack spacing={1.5}>
    <Typography sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{response.answer}</Typography>
    {response.findings.length > 0 && <Box>
      <Typography variant="subtitle2">{t('activityAssistant.findings', 'What the activity shows')}</Typography>
      <Box component="ul" sx={{ pl: 2.5, my: 0.5 }}>{response.findings.map((finding, index) =>
        <Typography component="li" variant="body2" key={index} sx={{ mb: 0.5 }}>{finding.text}</Typography>)}</Box>
    </Box>}
    {response.limitations.length > 0 && <Alert severity="info"><Stack spacing={0.5}>
      {response.limitations.map((text, index) => <Typography variant="body2" key={index}>{text}</Typography>)}
    </Stack></Alert>}
    {response.next_steps.length > 0 && <Box>
      <Typography variant="subtitle2">{t('activityAssistant.nextSteps', 'What you can do next')}</Typography>
      <Box component="ul" sx={{ pl: 2.5, my: 0.5 }}>{response.next_steps.map((text, index) =>
        <Typography component="li" variant="body2" key={index}>{text}</Typography>)}</Box>
    </Box>}
    {records.length > 0 && <Box component="details" open={Boolean(expanded.evidence)}>
      <Typography component="summary" variant="body2" sx={{ cursor: 'pointer', color: 'primary.main' }}
        onClick={event => { event.preventDefault(); onToggle('evidence'); }}>
        {t('activityAssistant.evidence', 'View supporting activity')} ({records.length})
      </Typography>
      <Stack spacing={1} sx={{ mt: 1 }}>{records.map(record => <Evidence key={record.id} record={record} t={t} language={language}
        expanded={Boolean(expanded[record.id])} onToggle={() => onToggle(record.id)} />)}</Stack>
    </Box>}
  </Stack>;
};

export default function ActivityAssistantDialog({ open, onClose, caseid, log = null, filters, contextLabel = '', organizationScope = '' }) {
  const { t, i18n } = useTranslation();
  const [question, setQuestion] = useState('');
  const [messages, setMessages] = useState([]);
  const [expandedMessages, setExpandedMessages] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const request = useRef(null);
  const sequence = useRef(0);
  const conversationEnd = useRef(null);
  const historyFilters = getActionHistoryScope(filters);
  const hasFilters = Object.keys(historyFilters).length > 0;
  const scope = `${organizationScope}:${caseid}:${log?.id || ''}:${JSON.stringify(historyFilters)}`;

  useEffect(() => {
    setQuestion('');
    setMessages([]);
    setExpandedMessages({});
    setError('');
  }, [scope]);

  useEffect(() => {
    setBusy(false);
    return () => { sequence.current += 1; request.current?.abort(); };
  }, [open, scope]);

  useEffect(() => {
    if (open) conversationEnd.current?.scrollIntoView?.({ block: 'nearest' });
  }, [messages, busy, open]);

  const submit = async event => {
    event.preventDefault();
    if (!question.trim() || busy || !caseid) return;
    const current = ++sequence.current;
    const controller = new AbortController();
    request.current = controller;
    const timer = setTimeout(() => controller.abort(), 120000);
    const submitted = question.trim();
    setError('');
    setBusy(true);
    try {
      const history = messages.flatMap(message => [
        { role: 'user', text: message.question },
        { role: 'assistant', text: message.response.answer }
      ]).slice(-8);
      const response = await askActivityAssistant(caseid, {
        question: submitted, language: i18n.language, history,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
        ...(hasFilters ? { filters: historyFilters } : {}),
        ...(log?.id ? { log_id: log.id } : {})
      }, controller.signal);
      if (current !== sequence.current) return;
      setMessages(previous => [...previous, { question: submitted, response }]);
      setQuestion('');
    } catch (failure) {
      if (current !== sequence.current) return;
      const code = failure.code;
      setError(code === 'access_denied' ? 'accessError' : code === 'activity_unavailable' ? 'activityError' : 'error');
    } finally {
      clearTimeout(timer);
      if (current === sequence.current) setBusy(false);
    }
  };

  return <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth aria-labelledby="activity-assistant-title"
    PaperProps={{ sx: { borderRadius: 3, height: 'min(760px, 90vh)' } }}>
    <DialogTitle component="div" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
      <Typography id="activity-assistant-title" component="h2" variant="h6">{t('activityAssistant.title', 'Ask a question')}</Typography>
      <CloseButton onClick={onClose} ariaLabel={t('activityAssistant.close', 'Close assistant')} />
    </DialogTitle>
    <Box sx={{ px: 3, pb: 2 }}>
      <Chip size="small" label={contextLabel || (hasFilters ? t('activityAssistant.filteredContext', 'Current filtered history') : t('activityAssistant.projectContext', 'Activity in this project'))}
        sx={{ maxWidth: '100%', height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal', py: 0.5 } }} />
      {hasFilters && <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{t('activityAssistant.filterScope', 'Answers use the same filters as this history.')}</Typography>}
      {log?.timestamp && <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
        {formatActivityDate(log.local_timestamp || log.timestamp, i18n.language, t, log)}
      </Typography>}
    </Box>
    <Divider />
    <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 3, minHeight: 180 }}>
      {!messages.length && <Stack spacing={2}>
        <Typography color="text.secondary">{t('activityAssistant.intro', 'Ask what happened, where to find a result, or what to do next. The assistant checks the recorded activity available to you.')}</Typography>
        <Stack spacing={1} alignItems="flex-start">
          {[
            ['whatHappened', 'What happened?'],
            ['findResult', 'Where can I find the result?'],
            ['whyDifferent', 'Why is the result different from before?']
          ].map(([key, fallback]) => <Button key={key} variant="outlined" size="small" disabled={busy}
            onClick={() => setQuestion(t(`activityAssistant.${key}`, fallback))}>{t(`activityAssistant.${key}`, fallback)}</Button>)}
        </Stack>
      </Stack>}
      <Box role="log" aria-label={t('activityAssistant.conversation', 'Conversation')} aria-live="polite">
        <Stack spacing={3}>{messages.map((message, index) => <Stack spacing={1.5} key={index}>
          <Box sx={{ alignSelf: 'flex-end', bgcolor: 'action.selected', borderRadius: 2, px: 2, py: 1.25, maxWidth: '90%' }}>
            <Typography sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{message.question}</Typography>
          </Box>
          <Answer response={message.response} t={t} language={i18n.language} expanded={expandedMessages[index] || {}}
            onToggle={key => setExpandedMessages(previous => ({ ...previous,
              [index]: { ...previous[index], [key]: !previous[index]?.[key] } }))} />
        </Stack>)}</Stack>
      </Box>
      {busy && <Stack spacing={1}>
        <Typography variant="body2" sx={{ alignSelf: 'flex-end', overflowWrap: 'anywhere' }}>{question}</Typography>
        <Box role="status" sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <CircularProgress size={20} /><Typography variant="body2">{t('activityAssistant.checking', 'Checking the related activity…')}</Typography>
        </Box>
      </Stack>}
      {error && <Alert severity="error">{t(`activityAssistant.${error}`, {
        accessError: 'You no longer have access to this project. Your question has been kept.',
        activityError: 'This activity is no longer available. Close the assistant and choose another activity.',
        error: 'The assistant could not finish checking. Your question has been kept. Try sending it again.'
      }[error])}</Alert>}
      <div ref={conversationEnd} />
    </DialogContent>
    <Divider />
    <Box component="form" onSubmit={submit} sx={{ p: 2.5 }}>
      <TextField autoFocus fullWidth multiline minRows={2} maxRows={5} label={t('activityAssistant.question', 'Your question')}
        value={question} disabled={busy} onChange={event => setQuestion(event.target.value)} inputProps={{ maxLength: 3000 }} />
      <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
        <Typography variant="caption" color="text.secondary">{t('activityAssistant.readOnly', 'The assistant checks activity without changing anything.')}</Typography>
        <Button type="submit" variant="contained" disabled={busy || !question.trim()} endIcon={<SendRoundedIcon />}>
          {t('activityAssistant.send', 'Send')}
        </Button>
      </Box>
    </Box>
  </Dialog>;
}
